import { useEffect, useCallback, useRef } from 'react'
import { useChatStore, type Message } from './stores/chatStore'
import { streamChatCompletion, createChatOnServer, fetchChats, fetchModels } from './services/api'
import Sidebar from './components/Sidebar'
import ChatView from './components/ChatView'
import MessageInput from './components/MessageInput'
import ImageGen from './components/ImageGen'

export default function App() {
  const currentChatId = useChatStore((s) => s.currentChatId)
  const chats = useChatStore((s) => s.chats)
  const selectedModel = useChatStore((s) => s.selectedModel)
  const streaming = useChatStore((s) => s.streaming)
  const sidebarOpen = useChatStore((s) => s.sidebarOpen)
  const setSidebarOpen = useChatStore((s) => s.setSidebarOpen)
  const createChat = useChatStore((s) => s.createChat)
  const addMessage = useChatStore((s) => s.addMessage)
  const updateLast = useChatStore((s) => s.updateLastAssistantMessage)
  const setStreaming = useChatStore((s) => s.setStreaming)
  const setModels = useChatStore((s) => s.setModels)
  const setChats = useChatStore((s) => s.setChats)

  const currentChat = chats.find((c) => c.id === currentChatId) ?? null
  const messages = currentChat?.messages ?? []

  const abortRef = useRef<AbortController | null>(null)
  const hasCreatedRef = useRef(false)

  useEffect(() => {
    fetchModels().then((d) => { if (d?.data?.length) setModels(d.data) }).catch(() => {})
    fetchChats().then((server) => {
      if (server?.length) {
        setChats(server.map((c: any) => ({
          id: c.id,
          title: c.title || 'New Chat',
          messages: c.messages || c.chat?.messages || [],
          createdAt: c.created_at ? c.created_at * 1000 : Date.now(),
          updatedAt: c.updated_at ? c.updated_at * 1000 : Date.now(),
        })))
      }
    }).catch(() => {})
  }, [])

  useEffect(() => {
    if (!hasCreatedRef.current && chats.length === 0) {
      hasCreatedRef.current = true
      setTimeout(() => createChat(), 100)
    }
  }, [chats.length])

  const handleSend = useCallback(async (text: string) => {
    let chatId = currentChatId
    if (!chatId) chatId = createChat()

    const userMsg: Message = { id: `${Date.now()}-u`, role: 'user', content: text }
    addMessage(userMsg)
    addMessage({ id: `${Date.now()}-a`, role: 'assistant', content: '' })

    setStreaming(true)
    const controller = new AbortController()
    abortRef.current = controller

    const state = useChatStore.getState()
    const chat = state.chats.find((c) => c.id === chatId)
    const msgs = [...(chat?.messages ?? [])]

    try {
      const fullContent = await streamChatCompletion(
        selectedModel,
        msgs.map((m) => ({ role: m.role, content: m.content })),
        (content) => updateLast(content),
        controller.signal
      )
      const final = useChatStore.getState()
      const finalChat = final.chats.find((c) => c.id === chatId)
      if (finalChat) {
        await createChatOnServer({
          id: finalChat.id,
          title: finalChat.title,
          messages: finalChat.messages.map((m) => ({ role: m.role, content: m.content })),
        })
      }
    } catch (err: any) {
      if (err?.name !== 'AbortError') updateLast('Error: ' + (err.message || 'Failed'))
    }

    setStreaming(false)
    abortRef.current = null
  }, [currentChatId, selectedModel, addMessage, updateLast, setStreaming, createChat])

  return (
    <div className="h-full flex bg-gray-950 text-gray-200 relative">
      <Sidebar />
      <main className="flex-1 flex flex-col min-w-0 relative">
        {/* Mobile header */}
        <div className="lg:hidden flex items-center gap-3 px-4 py-3 border-b border-gray-800">
          <button onClick={() => setSidebarOpen(!sidebarOpen)} className="p-2 rounded-lg hover:bg-gray-800 text-gray-400">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M3 12h18M3 6h18M3 18h18" />
            </svg>
          </button>
          <h1 className="font-semibold text-sm text-white">The Italian Job</h1>
        </div>

        {/* Stop button */}
        {streaming && (
          <div className="absolute top-3 left-1/2 -translate-x-1/2 z-10">
            <button
              onClick={() => { abortRef.current?.abort(); setStreaming(false) }}
              className="px-4 py-1.5 rounded-full bg-gray-850 border border-gray-700 text-[13px] text-gray-300 hover:bg-gray-800 hover:text-white transition-colors flex items-center gap-1.5 shadow-lg"
            >
              <span className="w-2 h-2 rounded-full bg-white" />
              Stop generating
            </button>
          </div>
        )}

        <ChatView messages={messages} streaming={streaming} />
        <MessageInput onSend={handleSend} />
        <ImageGen />
      </main>
    </div>
  )
}
